import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useFragment, useMutation} from "react-relay";
import graphql from "babel-plugin-relay/macro";
import {FormProvider, useForm} from "react-hook-form";
import {Card, Col, Form, Row, Tab} from "react-bootstrap";
import TabNav from "./components/TabNav";
import DetailsTab from "./DetailsTab";
import BillingAddressTab from "./BillingAddresses/BillingAddressTab";
import BillingGroupTab from "./BillingGroups/BillingGroupTab";
import PractitionerTab from "./Practitioners/PractitionerTab";
import LocationTab from "./Locations/LocationTab";
import PractitionerLocationTab from "./PractitionerLocations/PractitionerLocationTab";
import IconButton from "../../components/common/IconButton";
import {toast} from "react-toastify";

const InProgressRosterLoad = ({rosterLoad}) => {
    const data = useFragment(graphql`
    fragment InProgressRosterLoadFragment on RosterLoad {
        id
        status
        fileName
        downloadUrl
        createdOn
        createdBy {
            userName
        }
        legalContractLinesOfService {
            linesOfService {
                lineOfService {
                    name
                }
                contract {
                    name
                }
            }
        }
        addedBillingAddresses {
            id
            status
            keys {
                id
                displayOrder
                proposedValue
                propertyName
                mask {
                    type
                    value
                }
            }
            properties {
                ...usePropertyFieldFragment @relay(mask: false)
            }
        }
        changedBillingAddresses {
            id
            status
            keys {
                id
                displayOrder
                proposedValue
                propertyName
                mask {
                    type
                    value
                }
            }
            properties {
                ...usePropertyFieldFragment @relay(mask: false)
            }
        }
        addedBillingGroups {
            id
            status
            keys {
                id
                displayOrder
                proposedValue
                propertyName
                mask {
                    type
                    value
                }
            }
            properties {
                ...usePropertyFieldFragment @relay(mask: false)
            }
        }
        changedBillingGroups {
            id
            status
            keys {
                id
                displayOrder
                proposedValue
                propertyName
                mask {
                    type
                    value
                }
            }
            properties {
                ...usePropertyFieldFragment @relay(mask: false)
            }
        }
        addedLocations {
            id
            status
            keys {
                id
                displayOrder
                proposedValue
                propertyName
                mask {
                    type
                    value
                }
            }
            properties {
                ...usePropertyFieldFragment @relay(mask: false)
            }
        }
        changedLocations {
            id
            status
            keys {
                id
                displayOrder
                proposedValue
                propertyName
                mask {
                    type
                    value
                }
            }
            properties {
                ...usePropertyFieldFragment @relay(mask: false)
            }
        }
        addedPractitioners {
            id
            status
            keys {
                id
                displayOrder
                proposedValue
                propertyName
                mask {
                    type
                    value
                }
            }
            properties {
                ...usePropertyFieldFragment @relay(mask: false)
            }
        }
        changedPractitioners {
            id
            status
            keys {
                id
                displayOrder
                proposedValue
                propertyName
                mask {
                    type
                    value
                }
            }
            properties {
                ...usePropertyFieldFragment @relay(mask: false)
            }
        }
        addedPractitionerLocations {
            id
            status
            keys {
                id
                displayOrder
                proposedValue
                propertyName
                mask {
                    type
                    value
                }
            }
            properties {
                ...usePropertyFieldFragment @relay(mask: false)
            }
            parentLocation {
                id
            }
            parentPractitioner {
                id
            }
        }
        changedPractitionerLocations {
            id
            status
            keys {
                id
                displayOrder
                proposedValue
                propertyName
                mask {
                    type
                    value
                }
            }
            properties {
                ...usePropertyFieldFragment @relay(mask: false)
            }
            parentLocation {
                id
            }
            parentPractitioner {
                id
            }
        }
    }`, rosterLoad);
    
    const [commitIsInFlight, setCommitIsInFlight] = useState(false);
    const [autoAcceptAddedItemsIsInFlight, setAutoAcceptAddedItemsIsInFlight] = useState(false);
    const [autoAcceptChangedItemsIsInFlight, setAutoAcceptChangedItemsIsInFlight] = useState(false);
    
    const commit = graphql`
        mutation InProgressRosterLoadCommitMutation($id: ID!) {
            rosterLoad {
                commitRosterLoad(input: {rosterLoadId: $id}) {
                    rosterLoad {
                        id
                        fileName
                        status
                    }
                }
            }
        }`
    const methods = useForm({defaultValues:{...data}});
    const [commitMutation, anyMutationIsInFlight] = useMutation(commit);
    
    const autoAccept = graphql`
        mutation InProgressRosterLoadAutoAcceptMutation($id: ID!, $componentStatus: RosterLoadAutoAcceptComponentStatus!) {
            rosterLoad {
                autoAcceptComponents(input: {rosterLoadId: $id, componentStatus: $componentStatus}) {
                    unmodifiedCount
                    acceptedCount
                    acceptedComponentIds
                }
            }
        }`
    const [autoAcceptMutation] = useMutation(autoAccept);
    
    const updateFormItemStatuses = (statusesById, formFieldArray, formFieldArrayName) => {
        formFieldArray.forEach((x, idx) => {
            if (statusesById.has(x.id)) {
                methods.setValue(`${formFieldArrayName}[${idx}].status`, statusesById.get(x.id));
            }
        })
    }
    
    const updateItemStatuses = (items) => {
        const statusesById = new Map(items.map((x) => [x, "Accepted"]));
        const formData = methods.getValues();
        updateFormItemStatuses(statusesById, formData.addedBillingGroups, "addedBillingGroups");
        updateFormItemStatuses(statusesById, formData.addedBillingAddresses, "addedBillingAddresses");
        updateFormItemStatuses(statusesById, formData.addedLocations, "addedLocations");
        updateFormItemStatuses(statusesById, formData.addedPractitioners, "addedPractitioners");
        updateFormItemStatuses(statusesById, formData.addedPractitionerLocations, "addedPractitionerLocations");
        updateFormItemStatuses(statusesById, formData.changedBillingGroups, "changedBillingGroups");
        updateFormItemStatuses(statusesById, formData.changedBillingAddresses, "changedBillingAddresses");
        updateFormItemStatuses(statusesById, formData.changedLocations, "changedLocations");
        updateFormItemStatuses(statusesById, formData.changedPractitioners, "changedPractitioners");
        updateFormItemStatuses(statusesById, formData.changedPractitionerLocations, "changedPractitionerLocations");
    }
    
    const practitionersByKey = useMemo(() => {
        let y = new Map();
        data.addedPractitioners.concat(data.changedPractitioners).forEach((x) => {
            y.set(x.id, x);
        })
        return y;
    }, [data.addedPractitioners, data.changedPractitioners])
    const locationsByKey = useMemo(() => {
        let y = new Map();
        data.addedLocations.concat(data.changedLocations).forEach((x) => {
            y.set(x.id, x);
        })
        return y;
    }, [data.addedLocations, data.changedLocations]);

    const validateComponent = (addedName, changedName) => {
        const [added, changed] = methods.getValues([addedName, changedName]);
        const undecidedCount = added.filter(x => x.status === "Undecided").length + changed.filter(x => x.status === "Undecided").length
        if(undecidedCount > 0) {
            methods.setError(addedName, {type: 'custom'});
            return false;
        }
        methods.clearErrors([addedName]);
        return true;
    }

    const handleCommit = async () => {
        if (commitIsInFlight) return;
        const billingAddressesValid = validateComponent("addedBillingAddresses", "changedBillingAddresses");
        const billingGroupsValid = validateComponent("addedBillingGroups", "changedBillingGroups");
        const locationsValid = validateComponent("addedLocations", "changedLocations")
        const practitionersValid = validateComponent("addedPractitioners", "changedPractitioners");
        const practitionerLocationsValid = validateComponent("addedPractitionerLocations", "changedPractitionerLocations");
        if(billingAddressesValid && billingGroupsValid && locationsValid && practitionersValid && practitionerLocationsValid) {
            setCommitIsInFlight(true);
            commitMutation({
                variables: {
                    id: data.id
                }, onCompleted: (data) => {
                    setCommitIsInFlight(false);
                    if (data.rosterLoad) {
                        toast.success("Roster Load applied.");
                    } else {
                        toast.error("The Roster Load could not be applied.");
                    }
                }, onError: () => {
                    setCommitIsInFlight(false);
                }
            })
        }
    }

    const handleAutoAccept = async (componentStatus, isInFlight, setIsInFlight) => {
        if (isInFlight) return;
        setIsInFlight(true);
        autoAcceptMutation({
            variables: {
                id: data.id,
                componentStatus: componentStatus
            }, onCompleted: (data) => {
                setIsInFlight(false);
                if (data.rosterLoad) {
                    updateItemStatuses(data.rosterLoad.autoAcceptComponents.acceptedComponentIds);
                    toast.success(`${data.rosterLoad.autoAcceptComponents.acceptedCount} items were accepted and ${data.rosterLoad.autoAcceptComponents.unmodifiedCount} items could not be accepted.`);
                } else {
                    toast.error("Attempting to auto-accept items failed.");
                }
            }, onError: () => {
                setIsInFlight(false);
            }
        })
    }

    const handleAutoAcceptAddedItems = async () => {
        await handleAutoAccept("ADDED", autoAcceptAddedItemsIsInFlight, setAutoAcceptAddedItemsIsInFlight);
    }

    const handleAutoAcceptChangedItems = async () => {
        await handleAutoAccept("CHANGED", autoAcceptChangedItemsIsInFlight, setAutoAcceptChangedItemsIsInFlight);
    }

    return (
        <FormProvider {...methods}>
            <Form noValidate autoComplete={"off"}>
                <Tab.Container id={"rosterLoad"} defaultActiveKey={"rosterLoadDetails"}>
                    <TabNav rosterLoad={data.node}/>
                    <Tab.Content>
                        <Tab.Pane eventKey={"rosterLoadDetails"}>
                            <DetailsTab fileName={data.fileName} downloadUrl={data.downloadUrl}
                                        linesOfService={data.legalContractLinesOfService.map(l => l.linesOfService)}/>
                        </Tab.Pane>
                        <Tab.Pane eventKey={"billingAddresses"}>
                            <BillingAddressTab/>
                        </Tab.Pane>
                        <Tab.Pane eventKey={"billingGroups"}>
                            <BillingGroupTab/>
                        </Tab.Pane>
                        <Tab.Pane eventKey={"practitioners"}>
                            <PractitionerTab/>
                        </Tab.Pane>
                        <Tab.Pane eventKey={"locations"}>
                            <LocationTab/>
                        </Tab.Pane>
                        <Tab.Pane eventKey={"practitionerLocations"}>
                            <PractitionerLocationTab practitionersByKey={practitionersByKey} locationsByKey={locationsByKey} />
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
                <Card className={"mb-2"}>
                    <Card.Body>
                        <Row>
                            <Col>
                                <IconButton title={"apply roster"} type={"button"} onClick={handleCommit}
                                                icon={"check"}>
                                    {commitIsInFlight ? "Applying Roster..." : "Apply Roster"}
                                </IconButton>
                            </Col>
                            <Col className={"d-flex gap-2"}>
                                <IconButton title={"auto-accept added items"} type={"button"} onClick={handleAutoAcceptAddedItems}
                                            icon={"check-circle"}>
                                    {autoAcceptAddedItemsIsInFlight ? "Auto-Accepting Items..." : "Auto-Accept Added Items"}
                                </IconButton>
                                <IconButton title={"auto-accept changed items"} type={"button"} onClick={handleAutoAcceptChangedItems}
                                            icon={"check-circle"}>
                                    {autoAcceptChangedItemsIsInFlight ? "Auto-Accepting Items..." : "Auto-Accept Changed Items"}
                                </IconButton>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </Form>
        </FormProvider>
    )
}
InProgressRosterLoad.propTypes = {}
export default InProgressRosterLoad